import { Descriptions, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import {
  createEntranceExaminee,
  getEntranceDetail,
} from '../../../../api/placeTest.api';
import SimpleHeader from '../../../../components/layout/SimpleHeader';
import SelectStudentModal from '../../../../components/modals/SelectStudentModal';
import Button from '../../../../components/Button';
import useAxios from '../../../../hooks/useAxios';
import {
  IEntranceDetail,
  IEntranceDetailStudentInfo,
  IGetEntranceDetail,
} from '../../../../models/placeTest.model';
import config from '../../../../lib/config';
import Pagination from '../../../../components/Pagination';
import Loader from '../../../../components/Loader';
import ErrorResponse from '../../../../components/ErrorResponse';
import useNotification from '../../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../../lib/store';
import QueryString from 'qs';
import _ from 'lodash';
import useAuth from '../../../../hooks/useAuth';
import DownloadButton from '../../../../components/DownloadButton';
import exportTable from '../../../../lib/exportTable';
import styled from 'styled-components';

const Block = styled.div`
  .ant-table-tbody > tr.ant-table-row.canceled-row:hover > td {
    background: rgb(225, 225, 225);
  }
`;

interface IMatch {
  platScheduleCode: string;
}

const EntranceScheduleExaminee = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const match = useRouteMatch<IMatch>();
  const platScheduleCode = Number(match.params.platScheduleCode);
  const { successNotification } = useNotification();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetEntranceDetail>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [data, setData] = useState<IEntranceDetail>();
  const [page, setPage] = useState(1);
  const [selectStudentVisible, setSelectStudentVisible] = useState(false);

  useEffect(() => {
    if (platScheduleCode) {
      execute(getEntranceDetail(platScheduleCode));
    }
  }, [platScheduleCode]);

  useEffect(() => {
    if (res) {
      setData(res.data);
    }
  }, [res]);

  useEffect(() => {
    if (query.page) {
      setPage(Number(query.page));
    }
  }, [location]);

  useEffect(() => {
    if (submitRes) {
      execute(getEntranceDetail(platScheduleCode));
      setSelectStudentVisible(false);
      successNotification(t('common.message.create1'));
    }
  }, [submitRes]);

  const getColumns = () => {
    let columns: TableColumnsType<IEntranceDetailStudentInfo> = [
      {
        title: t('entrance.schedule.column14'),
        dataIndex: 'memberName',
        align: 'center',
        sorter: (a, b) => a.memberName?.localeCompare(b.memberName),
        render: (value, record) => `${record.memberName} (${record.webId})`,
      },
      {
        title: t('entrance.schedule.column11'),
        dataIndex: 'gradeCode',
        align: 'center',
        sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
        render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
      },
    ];

    if (data?.eduStepCode === 'EM') {
      const addColumns: TableColumnsType<IEntranceDetailStudentInfo> = [
        {
          title: t('entrance.schedule.10'),
          dataIndex: 'surveyScore',
          align: 'center',
          render: (value, record) => {
            if (value === null) {
              return record.surveyRequiredYn === 'Y' ? t('entrance.schedule.11') : t('entrance.schedule.text10');
            }
            return value;
          },
        },
          {
          title: 'Pre Test',
          dataIndex: 'preTestCompleteYn',
          align: 'center',
          sorter: (a, b) => a.preTestCompleteYn?.localeCompare(b.preTestCompleteYn),
          render: (value, record) => {
            if (value === 'Y') {
              return `${record.preTestName?.replace('Pre Test ', '')} (${Number(record.preTestScore)})`
            }

            return record.preTestRequiredYn === 'Y' ? t('entrance.schedule.text4') : t('entrance.schedule.text10');
          }
        },
        {
          title: 'OCEAN',
          dataIndex: 'oceanCompleteYn',
          align: 'center',
          sorter: (a, b) => a.oceanCompleteYn?.localeCompare(b.oceanCompleteYn),
          render: (value, record) => {
            if (value === 'Y') {
              return t('entrance.schedule.text3');
            }
            
            return record.oceanRequiredYn === 'Y' ? t('entrance.schedule.text4') : t('entrance.schedule.text10');
          }
        },
      ];
      columns = [...columns, ...addColumns];
    } else {
      const addColumns: TableColumnsType<IEntranceDetailStudentInfo> = [
        {
          title: 'Prediger',
          dataIndex: 'predigerCompleteYn',
          align: 'center',
          sorter: (a, b) => a.predigerCompleteYn?.localeCompare(b.predigerCompleteYn),
          render: value => value === 'Y'
            ? t('entrance.schedule.text3')
            : t('entrance.schedule.text4'),
        },
      ];
      columns = [...columns, ...addColumns];
    }

    let addColumns: TableColumnsType<IEntranceDetailStudentInfo> = [
      {
        title: 'Placement Test',
        dataIndex: 'placeTestCompleteYn',
        align: 'center',
        sorter: (a, b) =>
          a.placeTestCompleteYn?.localeCompare(b.placeTestCompleteYn),
        render: (value, record) => {
          if (value === 'Y') {
            return `${record.placeTestName?.replace(
              'Placement Test ',
              '',
            )} (${Number(record.placeTestScore)})`;
          }
  
          return t('entrance.schedule.text4');
        },
      },
      {
        title: t('entrance.schedule.column15'),
        dataIndex: 'price',
        align: 'center',
        sorter: (a: any, b: any) => a.price - b.price,
        render: (value) => (value ? Number(value).toLocaleString() : '-'),
      },
      {
        title: t('entrance.schedule.column16'),
        dataIndex: 'paymentCompleteAmount',
        align: 'center',
        render: (value, record) => {
          if (!Number(value)) {
            return (
              <div className="comm-btn__area justify-content-center">
                <Button
                  className="btn-basic small mint"
                  onClickCapture={(e) => {
                    e.stopPropagation();
                    history.push(
                      `/student/student?memberCode=${record.memberCode}&campusCode=${res?.data?.campusCode}&tabIndex=3`,
                    );
                  }}
                  text={t('entrance.schedule.button3')}
                />
              </div>
            );
          }
  
          return value ? Number(value).toLocaleString() : '-';
        },
      },
    ];
    columns = [...columns, ...addColumns];

    if (data?.eduStepCode === 'EM') {
      const addColumns: TableColumnsType<IEntranceDetailStudentInfo> = [
        {
          title: t('entrance.schedule.column17'),
          dataIndex: 'placeTestCode',
          align: 'center',
          render: (value, record) => record.speakingCompleteYn === 'Y'
            ? t('entrance.schedule.text5')
            : (
              <div className="comm-btn__area justify-content-center">
                <Button
                  className="btn-basic small mint"
                  onClickCapture={e => {
                    e.stopPropagation();
                    window.open(
                      `/cbt/speaking-test/exam?placeTestCode=${record.placeTestCode}&speakingExamCode=${record.speakingExamCode}&memberCode=${record.memberCode}&memberName=${record.memberName}&memberEngName=${record.memberEngName}&gradeCode=${record.gradeCode}`,
                      '_blank',
                    );
                  }}
                  text={t('entrance.schedule.button4')}
                  disabled={
                    !value ||
                    record.speakingExamCode === null ||
                    record.cancelYn === 'Y'
                  }
                />
              </div>
              ),
        },
      ];
      columns = [...columns, ...addColumns];
    } else {
      const addColumns: TableColumnsType<IEntranceDetailStudentInfo> = [
        {
          title: t('entrance.schedule.12'),
          dataIndex: 'placeTestCode',
          align: 'center',
          render: (value, record) => record.writingCompleteYn === 'Y'
            ? t('entrance.schedule.text5')
            : (
              <div className="comm-btn__area justify-content-center">
                <Button
                  className="btn-basic small mint"
                  onClickCapture={e => {
                    e.stopPropagation();
                    window.open(
                      `/cbt/speaking-test/exam?placeTestCode=${record.placeTestCode}&speakingExamCode=${record.writingExamCode}&memberCode=${record.memberCode}&memberName=${record.memberName}&memberEngName=${record.memberEngName}&gradeCode=${record.gradeCode}`,
                      '_blank',
                    );
                  }}
                  text={t('entrance.schedule.button4')}
                  disabled={
                    !value ||
                    record.writingExamCode === null ||
                    record.cancelYn === 'Y'
                  }
                />
              </div>
              ),
        },
      ];
      columns = [...columns, ...addColumns];
    }

    addColumns = [
      {
        title: t('entrance.schedule.column18'),
        dataIndex: 'registerYn',
        align: 'center',
        sorter: (a, b) => a.registerYn?.localeCompare(b.registerYn),
      },
      {
        title: t('entrance.schedule.column19'),
        dataIndex: 'cancelYn',
        align: 'center',
        sorter: (a, b) => a.cancelYn?.localeCompare(b.cancelYn),
      },  
    ];
    columns = [...columns, ...addColumns];  
    
    return columns;
  };
  
  const onExportTable = () => {
    if (!data) return;

    const _data: any = _.cloneDeep(data.studentInfo);
    _data.forEach((item: any) => {
      item.memberName = `${item.memberName} (${item.webId})`;
      item.gradeCode = getCommonCodeLabel('SY0006', item.gradeCode, 'ko');
      item.surveyScore = item.surveyScore === null
        ? item.surveyRequiredYn === 'Y'
          ? t('entrance.schedule.13')
          : t('entrance.schedule.text10')
        : item.surveyScore;
      item.preTestCompleteYn = item.preTestCompleteYn === 'Y'
        ? `${item.preTestName?.replace('Pre Test ', '')} (${Number(
            item.preTestScore,
          )})`
        : item.prtRequiredYn === 'Y'
          ? `${t('entrance.schedule.text4')} ${t('entrance.schedule.15')}`
          : `${t('entrance.schedule.text4')} ${t('entrance.schedule.16')}`;
      item.oceanCompleteYn = item.oceanCompleteYn === 'Y'
        ? t('entrance.schedule.text3')
        : item.oceanRequiredYn === 'Y'
          ? `${t('entrance.schedule.text4')} ${t('entrance.schedule.15')}`
          : `${t('entrance.schedule.text4')} ${t('entrance.schedule.16')}`;
      item.placeTestCompleteYn = item.placeTestCompleteYn === 'Y'
        ? `${item.placeTestName?.replace('Placement Test ', '')} (${Number(
            item.placeTestScore,
          )})`
        : t('entrance.schedule.text4');
      item.price = item.price ? Number(item.price).toLocaleString() : '-';
      item.paymentCompleteAmount = !Number(item.paymentCompleteAmount)
        ? '0'
        : item.paymentCompleteAmount
          ? Number(item.paymentCompleteAmount).toLocaleString()
          : '-';
      item.certifyCodeValue = item.certifyCodeValue
        ? item.certifyCodeValue
        : '-';
      item.memberEngName = item.memberEngName
        ? item.memberEngName
        : '-';
      item.predigerCompleteYn = item.predigerCompleteYn === 'Y'
        ? t('entrance.schedule.text3')
        : item.predigerRequiredYn === 'Y'
          ? `${t('entrance.schedule.text4')} ${t('entrance.schedule.15')}`
          : `${t('entrance.schedule.text4')} ${t('entrance.schedule.16')}`;
    });
    const filteredColumns = getColumns().filter(
      (item: any) => item.dataIndex !== 'placeTestCode',
    );
    // 칼럼 추가
    const memberEngNameColumn = {
      title: t('entrance.schedule.17'),
      dataIndex: 'memberEngName',
      key: 'memberEngName',
    };
    const schoolColumn = {
      title: t('student.student.column2'),
      dataIndex: 'schoolName',
      key: 'schoolName',
    };
    // 추가할 column 넣을 index찾기
    const insertIndex = filteredColumns.findIndex(
      (item: any) => item.dataIndex === 'gradeCode',
    );
    // 칼럼 삽입
    filteredColumns.splice(insertIndex, 0, schoolColumn);
    filteredColumns.splice(insertIndex, 0, memberEngNameColumn);

    filteredColumns.unshift({
      title: t('student.student.column64'),
      dataIndex: 'memberCode',
      key: 'memberCode',
    });
    filteredColumns.push({
      title: t('entrance.schedule.18'),
      dataIndex: 'certifyCodeValue',
      key: 'certifyCodeValue',
    });

    const currentDate = moment(new Date());
    exportTable(
      filteredColumns,
      _data,
      `${t('entrance.schedule.19')}_(${currentDate.format('YYYYMMDD')})`,
      t('entrance.schedule.19'),
      [15, 25, 15, 12, 12, 12, 15, 15, 15, 12, 12, 12],
    );
  };

  const onRegisterBtnClick = () => {
    if (!data || !platScheduleCode) return;
    setSelectStudentVisible(true);
  };

  const createExaminee = (memberCode: string) => {
    if (!data?.eduStepCode) return;
    
    submitExecute(createEntranceExaminee(platScheduleCode, {
      memberCode,
      eduStepCode: data?.eduStepCode,
    }));
  };

  return (
    <Block className="content scrollbar">
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <SimpleHeader
        parentMenu={t('common.menu.placementTest')}
        currentMenu={t('entrance.schedule.title3')}
      />

      <div className="comm-table__area rowType">
        <Descriptions bordered column={4} labelStyle={{ width: '150px' }} contentStyle={{width: '200px'}}>
          <Descriptions.Item label={t('common.label.1')}>
            {getCommonCodeLabel('PR0006', data?.eduStepCode || '', i18n.language)}
          </Descriptions.Item>
          <Descriptions.Item label={t('entrance.schedule.column20')}>
            {moment(data?.examDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('entrance.schedule.column2')}>
            {data?.srtTime}
          </Descriptions.Item>
          <Descriptions.Item label={t('entrance.schedule.8')}>
            {data?.closeYn}
          </Descriptions.Item>
          <Descriptions.Item label={t('entrance.schedule.column3')}>
            {data?.quota === 0 ? t('entrance.schedule.text1') : data?.quota}
          </Descriptions.Item>
          <Descriptions.Item label={t('entrance.schedule.column4')}>
            {data?.applyCnt}
          </Descriptions.Item>
          <Descriptions.Item label={t('entrance.schedule.column5')}>
            {data?.payCnt}
          </Descriptions.Item>
          <Descriptions.Item label={t('entrance.schedule.column6')}>
            {data?.testCnt}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => {
              if(query.startDate && query.endDate) {
                history.push(`/entrance/schedule?startDate=${query.startDate}&endDate=${query.endDate}`);
              } else {
                history.push('/entrance/schedule');
              }
            }}
            text={t('common.button.back')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onRegisterBtnClick}
            text={t('entrance.schedule.button2')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
      <div className="comm-table__area">
        <Table
          columns={getColumns()}
          dataSource={data?.studentInfo}
          pagination={{
            current: page,
            pageSize: config.pageSize,
          }}
          rowClassName={(record) =>
            record.cancelYn === 'Y' ? 'chosen' : 'pointer'
          }
          onRow={(record) => {
            return {
              onClick: () => {
                if(query.startDate && query.endDate) {
                  history.push(`/entrance/schedule/${platScheduleCode}/examinee/${record.platRequireCode}?startDate=${query.startDate}&endDate=${query.endDate}`);
                } else {
                  history.push(`/entrance/schedule/${platScheduleCode}/examinee/${record.platRequireCode}`);
                }
              },
            };
          }}
        />
      </div>
      <div className="comm-bx__between">
        <DownloadButton onClick={onExportTable} />
        <div className="common-paging center">
          <Pagination
            count={data?.studentInfo.length || 0}
            page={page}
            param={{}}
            path={`/entrance/schedule/${platScheduleCode}/examinee`}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onRegisterBtnClick}
            text={t('entrance.schedule.button2')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {selectStudentVisible && (
        <SelectStudentModal
          visible={selectStudentVisible}
          setVisible={setSelectStudentVisible}
          selectStudent={createExaminee}
        />
      )}
    </Block>
  );
};

export default EntranceScheduleExaminee;
